import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Calendar, EnvelopeSimple, Phone, Buildings, Trash, ChatCircleText, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new",       label: "New",       color: "#FF2D2D" },
  { value: "contacted", label: "Contacted", color: "#FFA94D" },
  { value: "scheduled", label: "Scheduled", color: "#4DABF7" },
  { value: "done",      label: "Done",      color: "#28C878" },
  { value: "declined",  label: "Declined",  color: "#666" },
];

const TYPE_LABEL = {
  discovery: "Discovery call",
  project_consult: "Project consult",
  revision_review: "Revision review",
  delivery_walkthrough: "Delivery walkthrough",
  quote_request: "Quote request",
  other: "Other",
};

export default function AdminMeetings() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get(`/admin/meeting-requests${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/meeting-requests/summary"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load meetings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/meeting-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this meeting request? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/meeting-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch (_) {
      toast.error("Failed to delete");
    }
  };

  const meetingLink = `${window.location.origin}/meet`;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap" data-testid="admin-meetings-header">
        <div>
          <h1 className="font-display text-3xl uppercase">Meetings</h1>
          <div className="text-xs text-[#a3a3a3] mt-1">
            Requests submitted through the hidden intake page.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigator.clipboard.writeText(meetingLink).then(() => toast.success("Meeting link copied"))}
            data-testid="copy-meeting-link"
            className="border border-[#262626] hover:border-[#28C878] hover:text-[#28C878] text-[#a3a3a3] px-3 py-2 text-xs uppercase tracking-wider transition-colors flex items-center gap-2"
            title={meetingLink}
          >
            <ArrowsClockwise size={12} /> Copy /meet link
          </button>
          <button onClick={load} data-testid="meetings-reload"
            className="border border-[#262626] hover:border-[#FF2D2D] hover:text-[#FF2D2D] text-[#a3a3a3] px-3 py-2 text-xs uppercase tracking-wider transition-colors">
            Reload
          </button>
        </div>
      </div>

      {/* Status pill filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="status-filters">
        <button onClick={() => setFilter("")}
          className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === "" ? "border-[#FF2D2D] text-[#FF2D2D]" : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D]"}`}>
          All ({Object.values(summary).reduce((a, b) => a + b, 0)})
        </button>
        {STATUSES.map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)}
            data-testid={`filter-${s.value}`}
            className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === s.value ? "text-white" : "text-[#a3a3a3] hover:text-white"}`}
            style={{ borderColor: filter === s.value ? s.color : "#262626" }}>
            <span style={{ color: filter === s.value ? s.color : undefined }}>{s.label}</span>
            <span className="ml-1 tabular-nums opacity-60">({summary[s.value] || 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#a3a3a3]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-[#262626] bg-[#0a0a0a] p-12 text-center" data-testid="meetings-empty">
          <ChatCircleText size={32} weight="duotone" className="text-[#FF2D2D] mx-auto mb-3" />
          <div className="font-display text-xl uppercase">No requests yet</div>
          <div className="text-xs text-[#a3a3a3] mt-2 max-w-md mx-auto">
            Share your <code className="text-[#FF2D2D]">{meetingLink}</code> link in a signature / DM / email — new requests will land here.
          </div>
        </div>
      ) : (
        <div className="border border-[#262626] divide-y divide-[#262626] bg-[#0a0a0a]" data-testid="meetings-list">
          {items.map((r) => {
            const status = STATUSES.find((s) => s.value === r.status) || STATUSES[0];
            const isOpen = expanded === r.id;
            return (
              <div key={r.id} data-testid={`meeting-row-${r.id}`}>
                <button onClick={() => setExpanded(isOpen ? null : r.id)}
                  className="w-full text-left p-4 hover:bg-[#121212] transition-colors flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 border"
                    style={{ borderColor: `${status.color}55`, color: status.color }}>
                    {status.label}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#666]">
                    {TYPE_LABEL[r.meeting_type] || r.meeting_type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">
                      {r.name}
                      {r.company && <span className="text-[#a3a3a3]"> · {r.company}</span>}
                    </div>
                    <div className="text-[11px] text-[#a3a3a3] truncate">{r.email}</div>
                  </div>
                  <div className="text-[10px] text-[#666] uppercase tracking-wider shrink-0">
                    {(r.created_at || "").slice(0, 10)}
                  </div>
                </button>

                {isOpen && (
                  <div className="p-5 border-t border-[#262626] bg-[#050505] space-y-4" data-testid={`meeting-detail-${r.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <Info icon={EnvelopeSimple} label="Email" href={`mailto:${r.email}`}>{r.email}</Info>
                      {r.phone && <Info icon={Phone} label="Phone" href={`tel:${r.phone}`}>{r.phone}</Info>}
                      {r.company && <Info icon={Buildings} label="Company">{r.company}</Info>}
                      {(r.preferred_date || r.preferred_time) && (
                        <Info icon={Calendar} label="Preferred">
                          {r.preferred_date || "—"} {r.preferred_time || ""}
                        </Info>
                      )}
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-2">Topic</div>
                      <div className="text-sm text-white whitespace-pre-wrap border-l-2 border-[#FF2D2D] pl-3">
                        {r.topic}
                      </div>
                    </div>

                    {r.admin_note && (
                      <div className="border border-[#262626] bg-[#0a0a0a] p-3">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-1">Internal note</div>
                        <div className="text-xs text-white whitespace-pre-wrap">{r.admin_note}</div>
                      </div>
                    )}

                    <div className="flex items-center gap-1 flex-wrap pt-2 border-t border-[#262626]">
                      {STATUSES.map((s) => (
                        <button key={s.value} onClick={() => updateStatus(r.id, s.value)}
                          disabled={r.status === s.value}
                          data-testid={`set-${s.value}-${r.id}`}
                          className={`text-[10px] uppercase tracking-wider px-2.5 py-1.5 border transition-colors ${r.status === s.value ? "opacity-40 cursor-default" : "text-[#a3a3a3] hover:text-white"}`}
                          style={{
                            borderColor: r.status === s.value ? s.color : "#262626",
                            color: r.status === s.value ? s.color : undefined,
                          }}>
                          {s.label}
                        </button>
                      ))}
                      <a href={`mailto:${r.email}?subject=Re: your meeting request (Vision Grafix)`}
                        className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-[#FF2D2D]/40 text-[#FF2D2D] hover:bg-[#FF2D2D]/10 flex items-center gap-1"
                        data-testid={`reply-${r.id}`}>
                        <EnvelopeSimple size={10} /> Reply
                      </a>
                      <button onClick={() => remove(r.id)}
                        title="Delete"
                        data-testid={`delete-${r.id}`}
                        className="ml-auto text-[10px] uppercase tracking-wider border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] px-2 py-1.5">
                        <Trash size={10} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

function Info({ icon: Icon, label, children, href }) {
  const Wrap = href ? "a" : "div";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="bold" />}
        {label}
      </div>
      <Wrap href={href} className={`text-white ${href ? "hover:text-[#FF2D2D]" : ""}`}>{children}</Wrap>
    </div>
  );
}
